import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileSignature, Plus } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/misc';
import { usePagination } from '@/hooks/use-pagination';
import { useDebounce } from '@/hooks/use-debounce';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useContracts } from './api';
import { ContractDialog } from './contract-dialog';
import {
  CONTRACT_STATUS_LABELS,
  CONTRACT_STATUS_VARIANT,
  CONTRACT_TYPE_LABELS,
} from './labels';
import type { Contract } from './types';

export function ContractsPage() {
  const navigate = useNavigate();
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [expiring, setExpiring] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounced = useDebounce(search, 300);

  const { data, isLoading, error, refetch } = useContracts({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    status: status || undefined,
    expiringInDays: expiring ? 90 : undefined,
  });

  const columns: Column<Contract>[] = [
    {
      key: 'name',
      header: 'Contract',
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{row.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            <span className="font-mono">{row.number}</span> · {row.company.name}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      className: 'text-sm text-muted-foreground',
      cell: (row) => CONTRACT_TYPE_LABELS[row.contractType],
    },
    {
      key: 'value',
      header: 'Value',
      className: 'tabular text-sm font-medium',
      cell: (row) => formatMoney(row.value, row.currency),
    },
    {
      key: 'term',
      header: 'Term',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => `${formatDate(row.startDate)} – ${formatDate(row.endDate)}`,
    },
    {
      key: 'remaining',
      header: 'Ends',
      cell: (row) => (
        <span
          className={cn(
            'text-sm',
            row.daysRemaining < 0
              ? 'text-destructive'
              : row.daysRemaining <= 30
                ? 'font-medium text-amber-600'
                : 'text-muted-foreground',
          )}
        >
          {row.daysRemaining < 0
            ? `${Math.abs(row.daysRemaining)}d ago`
            : `in ${row.daysRemaining}d`}
        </span>
      ),
    },
    {
      key: 'tickets',
      header: 'Tickets',
      className: 'tabular text-sm text-muted-foreground',
      cell: (row) => row.ticketCount,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <Badge variant={CONTRACT_STATUS_VARIANT[row.status]}>
            {CONTRACT_STATUS_LABELS[row.status]}
          </Badge>
          {row.autoRenew && (
            <span className="text-[10px] text-muted-foreground">auto-renews</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Contracts"
        description="AMCs and support retainers — what is covered, how many hours are left, and when it ends."
        actions={
          <Can permission="service.contract.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New contract
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search name, number or client…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          className="h-9 max-w-xs"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[9rem]"
        >
          <option value="">All statuses</option>
          {Object.entries(CONTRACT_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Button
          size="sm"
          variant={expiring ? 'secondary' : 'outline'}
          onClick={() => {
            setExpiring((value) => !value);
            pagination.reset();
          }}
        >
          Expiring in 90 days
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load contracts.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => navigate(`/service/contracts/${row.id}`)}
        emptyTitle="No contracts yet"
        emptyDescription="A contract is what tells an agent what a client is entitled to before they promise anything."
        emptyAction={
          <Can permission="service.contract.create">
            <Button onClick={() => setCreating(true)}>
              <FileSignature className="h-4 w-4" />
              New contract
            </Button>
          </Can>
        }
      />

      {creating && (
        <ContractDialog
          onClose={() => setCreating(false)}
          onSaved={(contract) => navigate(`/service/contracts/${contract.id}`)}
        />
      )}
    </div>
  );
}
